import cloneDeep from 'lodash/cloneDeep';

import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChange,
  ViewChild,
} from '@angular/core';

import { combineLatest, forkJoin, Observable, Subject } from 'rxjs';

import { TUser } from 'src/app/project/modules/user/user.model';

import { ActiveService } from 'src/app/project/services/active/active.service';
import { PlanPinsService } from '../../plan/plan-pins.service';
import { SiteTableEventsService } from './site-table-events.service';

import { GET_ACTIVE_PLAN } from 'src/app/project/modules/plan/plan.store';
import { RemoveWorkspacePoints } from 'src/app/project/modules/points/points.actions';
import { SET_POINTS } from 'src/app/project/modules/points/points.store';
import { GET_USER } from 'src/app/project/modules/user/user.store';

import { PreferencesService } from 'src/app/project/modules/preferences/preferences-service/preferences.service';
import { UserService } from 'src/app/project/modules/user/user.service';
import {
  TWorkspaceDataResponse,
  WorkspaceService,
} from 'src/app/project/modules/workspace/workspace.service';
import { SiteService } from '../site.service';

import { WindowService } from '@core/services';
import { select, Store } from '@ngrx/store';
import { TAllUsers } from '@project/view-models';
import { catchError, filter, map, takeUntil, tap } from 'rxjs/operators';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { WorkspaceApiProviderService } from 'src/app/project/data-providers/api-providers/workspace-api-provider/workspace-api-provider.service';
import { ResponseErrorService } from 'src/app/project/modules/errors/response-error.service';
import { TActive } from 'src/app/project/services/active/active.model';
import { ScrollHandlingService } from 'src/app/project/services/infinite-scrolling/scroll-handling.service';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { CombineCustomFieldsService } from '../../custom-fields/combine-custom-fields/combine-custom-fields.service';
import { TCustomField } from '../../custom-fields/custom-fields.model';
import { CustomFieldModelFactory } from '../../custom-fields/utils/custom-field-model-factory';
import { QuickFilterService } from '../../filters/filters-api/search/quick-filter-service';
import { startPointModalOpenTimer } from '../../points/point-full-modal/point-modal-open-monitor';
import { buildSlowModalOpenReport } from '../../points/point-full-modal/utils/build-slow-modal-open-report';
import { PointAttachmentsService } from '../../points/point-modal/point-attachments/point-attachments.service';
import { PointSearchService } from '../../points/point-search/point-search.service';
import { SavedViewsService } from '../../saved-views/saved-views.service';
import { SiteOptionsService } from '../site-options/site-options.service';
import { CustomTableService } from './custom-table/custom-table.service';
import { TableLoadingService } from './custom-table/table-loading.service';
import Table from './custom-table/table/Table';
import { SiteTableService } from './site-table.service';
import { CLEAR_COLLAPSED_GROUPS } from './table.ui.store';

@Component({
  selector: 'pp-site-table',
  templateUrl: './site-table.component.html',
  styleUrls: ['./site-table.component.scss'],
  standalone: false,
})
export class SiteTableComponent implements OnDestroy, AfterViewInit, OnChanges {
  @Input() ppWorkspaceId: string = null;
  @Input() ppHidden = false;
  @Input() ppFullWidth: boolean;
  @Input() ppHalfWidth: boolean;

  @ViewChild('tableWrapper', { static: true }) tableWrapperElement: ElementRef;
  @ViewChild('table', { static: true }) tableElement: ElementRef;

  private readonly destroy$ = new Subject<void>();

  activePlan = GET_ACTIVE_PLAN();

  public users: TAllUsers = {};
  public user: TUser;

  customFields = null;
  private active$: Observable<TActive>;
  private table: Table;
  isTableLoading: Observable<boolean>;
  EIconPath = EIconPath;
  iconMap = iconMap;

  constructor(
    private store: Store<{ active: TActive }>,
    private siteTableEventsService: SiteTableEventsService,
    private activeService: ActiveService,
    private planPinsService: PlanPinsService,
    private preferencesService: PreferencesService,
    private userService: UserService,
    private siteService: SiteService,
    private workspaceService: WorkspaceService,
    private workspaceApiProviderService: WorkspaceApiProviderService,
    private responseErrorService: ResponseErrorService,
    private pointAttachmentsService: PointAttachmentsService,
    private siteOptionsService: SiteOptionsService,
    private siteTableService: SiteTableService,
    private dropdownService: DropdownService,
    private customTableService: CustomTableService,
    private savedViewsService: SavedViewsService,
    private pointSearchService: PointSearchService,
    private scrollHandlingService: ScrollHandlingService,
    private tableLoadingService: TableLoadingService,
    private quickFilterService: QuickFilterService,
    private combineCustomFieldsService: CombineCustomFieldsService,
    private windowService: WindowService,
  ) {
    this.isTableLoading = this.tableLoadingService.isTableLoading$;
    this.active$ = this.store.pipe(select(EStore.ACTIVE));

    combineLatest([this.active$, this.tableLoadingService.isTableLoading$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([, loading]) => !loading),
      )
      .subscribe(([active]) => {
        if (this.table && active._id) {
          this.table.setActivePointId(active._id);
        }
      });

    this.tableLoadingService.toggleSpinner(true);
  }

  ngAfterViewInit() {
    this.table = this.createTableInstance();

    this.tableWrapperElement.nativeElement.replaceChild(
      this.table.element,
      this.tableElement.nativeElement,
    );

    this.loadWorkspace();
  }

  ngOnChanges(changes: { [key: string]: SimpleChange }) {
    if (changes.ppWorkspaceId && !changes.ppWorkspaceId.isFirstChange()) {
      const workspaceId = this.activeService.getActiveWorkspaceId();
      const previousWorkspaceId: string = changes.ppWorkspaceId.previousValue;
      const table = this.customTableService.getTable();

      if (previousWorkspaceId && previousWorkspaceId !== workspaceId) {
        this.clearWorkspaceData(previousWorkspaceId);
      }

      table.clear();
      table.setWorkspaceId(workspaceId);

      this.loadWorkspace();
    } else if (changes.ppFullWidth || changes.ppHalfWidth) {
      if (this.table) {
        this.table.load({ keepScrollPosition: true });
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.table?.destroy();
    this.table = null;

    if (this.ppWorkspaceId) {
      this.clearWorkspaceData(this.ppWorkspaceId);
    }
  }

  private clearWorkspaceData(workspaceId: string): void {
    this.pointSearchService.removeWorkspaceData(workspaceId);
    this.store.dispatch(new RemoveWorkspacePoints({ workspaceId }));
  }

  loadWorkspace(): void {
    const table = this.customTableService.getTable();

    this.tableLoadingService.toggleSpinner(true);
    table.startLoadMonitor();

    this.quickFilterService.resetFilters();
    const workspaceId = this.activeService.getActiveWorkspaceId();

    this.siteTableService.setTableLoaded(false);
    this.savedViewsService.setSelectedViewId(workspaceId, null);

    SET_POINTS([]);
    CLEAR_COLLAPSED_GROUPS();
    this.customTableService.clearGroupSizes();

    forkJoin([
      this.workspaceService.generateWorkspace(workspaceId),
      this.workspaceService.fetchWorkspaceData(workspaceId),
      this.userService.fetchUser(),
      this.customTableService.fetchFieldsLayout(workspaceId),
    ])
      .pipe(
        takeUntil(this.destroy$),
        tap(([, workspaceDataResponse]) => {
          this.handleLoadWorkspace({
            workspaceDataResponse,
            table,
            workspaceId,
          });
          table.reload({
            reset: true,
            isOverview: false,
          });
        }),
      )
      .subscribe();
  }

  private handleLoadWorkspace({
    workspaceDataResponse,
    table,
    workspaceId,
  }: {
    workspaceDataResponse: TWorkspaceDataResponse;
    table: Table;
    workspaceId: string;
  }): void {
    const savedViews = this.savedViewsService.getSavedViews(workspaceId);
    const workspacePreferences = this.preferencesService.getCurrentWorkspaceLayout({
      savedViews,
    });

    const workspaceUsers = workspaceDataResponse.workspaceUsers;
    this.siteTableService.processPreferencesResponse(cloneDeep(workspacePreferences), workspaceId);

    const sortButton = this.siteOptionsService.getSortButton();
    const groupButton = this.siteOptionsService.getGroupButton();

    this.customFields = workspaceDataResponse.workspace.customFields;

    if (sortButton) {
      sortButton.update();
    }

    if (groupButton) {
      groupButton.update();
    }

    this.customTableService.checkColumns(this.customFields, this.ppWorkspaceId);
    table.updateVisibleColumnIndexes();
    table.setKeyword('');
    table.updateWidth();
    table.setUsers(workspaceUsers);

    this.siteService.setTableFetched(true);
    this.siteTableService.setTableLoaded(true);
  }

  private createTableInstance(): Table {
    return new Table(false, {
      dropdownService: this.dropdownService,
      workspaceId: this.ppWorkspaceId,
      windowService: this.windowService,
      selectPointCallback: (_point): void => {
        this.siteTableEventsService.togglePointSelection({
          workspaceId: _point.workspaceRef.id,
          _id: _point._id,
        });
      },
      selectAllPointsCallback: (selectAll: boolean): void => {
        this.siteTableEventsService.toggleAllPointsSelection(selectAll);
      },
      openPointCallback: (_point): void => {
        const pointSwitchingBlocked = this.pointAttachmentsService.blocksPointSwitching();

        if (!this.activePlan.active && !pointSwitchingBlocked) {
          startPointModalOpenTimer(() =>
            buildSlowModalOpenReport({
              url: window.location.href,
              overview: false,
              workspaceId: _point.workspaceRef?.id,
              pointId: _point._id,
              user: GET_USER(),
            }),
          );

          this.siteTableEventsService.openPoint(
            { _id: _point._id, workspaceId: _point.workspaceRef?.id },
            { isOverview: false },
          );
        }
      },
      savePreferencesCallback: (): void => {
        if (this.ppWorkspaceId) {
          this.preferencesService.savePreferences(this.ppWorkspaceId);
        }
      },
      highlightPinCallback: (_pointId: number): void => {
        this.planPinsService.enlargePin(_pointId);
      },
      dehighlightPinCallback: (): void => {
        this.planPinsService.restorePin();
      },
      getCustomFieldsCallback: (): Observable<TCustomField[]> =>
        this.workspaceApiProviderService.fetchWorkspace(this.ppWorkspaceId).pipe(
          takeUntil(this.destroy$),
          map((response) =>
            response.customFields.map((customField) =>
              CustomFieldModelFactory.createFromDTO(customField),
            ),
          ),
          catchError((error) =>
            this.responseErrorService.handleRequestError(error, 'Failed to fetch custom fields'),
          ),
        ),
      pointAttachmentsService: this.pointAttachmentsService,
      activePointId: this.activeService.getActivePointId(),
      siteOptionsService: this.siteOptionsService,
      customTableService: this.customTableService,
      pointSearchService: this.pointSearchService,
      scrollHandlingService: this.scrollHandlingService,
      tableLoadingService: this.tableLoadingService,
      combineCustomFieldsService: this.combineCustomFieldsService,
    });
  }
}
